import { native } from '@muzly/shared';
import type {
  NativeInvokeOptions,
  NativeParams,
  NativeResult,
} from '@muzly/shared';

/**
 * readTakeBytes — 기기에 쓰인 테이크 WAV 를 웹으로 가져온다.
 *
 * 왜 웹이 바이트를 들고 있나: 업로드를 웹 층에서 하기로 했고(플랜 § Decisions), WebView 는
 * https origin 이라 네이티브 샌드박스의 `file://` 을 직접 못 읽는다. 그래서 완성된 파일을
 * **테이크당 한 번** 브리지로 건네받는다.
 *
 * GOAL.md 의 불변식("Raw PCM never *continuously* crosses to JS")은 실시간 스트림을 막는
 * 것이고, 끝난 파일 한 건을 옮기는 이 경로는 그 밖이다.
 */

/**
 * 한 테이크에서 읽어 올 최대 바이트.
 *
 * 가장 긴 테이크는 지속음 30초(`SUSTAIN_LIMIT_SEC`)이고, 48kHz·16-bit 모노로 ~2.9MB 다.
 * 상한은 그 위 한 뼘이면 족하다 — 크게 잡을수록 손해다. 이 바이트는 base64 로 4/3 배가 된 뒤
 * **RPC 응답 스크립트의 소스에 통째로 박혀** WebView 로 주입되므로(`native-host` 브리지),
 * 피크에는 같은 오디오의 사본이 여러 겹 살아 있다. 넘는 파일은 읽지 않고 버린다.
 */
export const MAX_TAKE_BYTES = 4 * 1024 * 1024;

/**
 * 이 한 건에 주는 예산. 기본값(5초 · 3회 재시도)을 그대로 쓰면 안 된다 — 느린 기기에서
 * 한 번 넘기는 순간 같은 파일을 다시 읽고 다시 base64로 만들어 다시 브리지에 올리고,
 * 백오프까지 붙어 15초를 넘긴다. 그 15초가 회차 마감이 업로드에 주기로 한 예산 전부다.
 *
 * `retries` 는 **총 시도 횟수**다(기본 3). 1 은 재시도 없음이다 — 이 RPC 는 파일 읽기라
 * 실패가 일시적이지 않다(없거나, 너무 크거나, 못 읽거나). 다시 물어서 답이 달라지는
 * 종류가 아니다.
 */
const READ_INVOKE: NativeInvokeOptions = { timeout: 12_000, retries: 1 };

/**
 * 이 deps 가 부르는 브리지 메서드는 하나뿐이라 제네릭을 두지 않는다. `native.invoke` 는
 * params 유무로 갈리는 오버로드라, 제네릭 인자를 그대로 흘리면 어느 갈래인지 못 정한다.
 */
export interface ReadTakeBytesDeps {
  invoke: (
    method: 'readRecording',
    params: NativeParams<'readRecording'>,
    options?: NativeInvokeOptions,
  ) => Promise<NativeResult<'readRecording'>>;
}

const decodeBase64 = (base64: string): Uint8Array => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

/**
 * 실패는 전부 `null` 이다 — 파일이 없든, 상한을 넘든, RPC 가 없든, 문자열이 깨졌든.
 * 원음을 못 읽는 것은 진단 실패가 아니고, 호출부는 `mediaId` 없이 마감하면 된다.
 */
export const readTakeBytes = async (
  deps: ReadTakeBytesDeps,
  uri: string,
): Promise<Uint8Array | null> => {
  try {
    const read = await deps.invoke(
      'readRecording',
      { uri, maxBytes: MAX_TAKE_BYTES },
      READ_INVOKE,
    );
    if (read === null) {
      return null;
    }
    // 길이를 **디코드 전에** 본다. 잘린 응답이면 수 MB 를 다 풀어 놓고 버리는 셈이라,
    // 애초에 못 맞는 문자열은 여기서 끊는다(base64 는 3바이트당 4글자, 패딩 포함).
    if (read.base64.length !== Math.ceil(read.byteLength / 3) * 4) {
      return null;
    }
    const bytes = decodeBase64(read.base64);
    // 브리지는 문자열을 나른다. 잘린 문자열도 디코드는 되지만 **짧은 파일**이 되고, 그러면
    // 서명은 맞는데 내용이 틀린 오디오가 올라간다 — 실패보다 나쁘다, 아무도 모르니까.
    return bytes.byteLength === read.byteLength ? bytes : null;
  } catch {
    return null;
  }
};

/**
 * 실제 브리지에 물린 deps.
 *
 * **`native.invoke` 를 값으로 그냥 넘기면 안 된다.** 그것은 화살표 프로퍼티가 아니라
 * `Native` 의 프로토타입 메서드고 본문이 `this.isReady`·`this.dispatchRequest` 를 쓴다.
 * 떼어서 넘기면 ESM strict 에서 `this` 가 `undefined` 라 TypeError 가 나고, 위 `catch` 가
 * 그것을 삼켜 **기기에서 언제나 `null`** 이 된다 — 업로드가 통째로 죽는데 아무 신호도 없다.
 */
export const nativeReadDeps = (): ReadTakeBytesDeps => ({
  invoke: (method, params, options) => native.invoke(method, params, options),
});
